import React, { useEffect, useState } from 'react';
import { testPromiseWithResolvers, ensurePromiseWithResolvers } from '../utils/polyfill-test';

interface TestResult {
  test: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: any;
}

const PolyfillTestPage: React.FC = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const runTests = async () => {
      const results: TestResult[] = [];

      // Test 1: Check if Promise.withResolvers exists
      try {
        const exists = testPromiseWithResolvers();
        results.push({
          test: 'Promise.withResolvers 存在性检查',
          status: exists ? 'success' : 'warning',
          message: exists ? 'Promise.withResolvers 方法可用' : 'Promise.withResolvers 方法不可用',
          details: { exists }
        });
      } catch (error) {
        results.push({
          test: 'Promise.withResolvers 存在性检查',
          status: 'error',
          message: '检查过程中发生错误',
          details: error
        });
      }

      // Test 2: Try to use Promise.withResolvers directly
      try {
        const { promise, resolve, reject } = Promise.withResolvers<string>();
        resolve('测试成功');
        const result = await promise;
        results.push({
          test: 'Promise.withResolvers 功能测试',
          status: 'success',
          message: `功能测试通过: ${result}`,
          details: { result }
        });
      } catch (error) {
        results.push({
          test: 'Promise.withResolvers 功能测试',
          status: 'error',
          message: '功能测试失败',
          details: error
        });

        // Test 3: Force injection if direct test failed
        try {
          ensurePromiseWithResolvers();
          const { promise, resolve, reject } = Promise.withResolvers<string>();
          resolve('强制注入后测试成功');
          const result = await promise;
          results.push({
            test: '强制注入后功能测试',
            status: 'success',
            message: `强制注入后测试通过: ${result}`,
            details: { result }
          });
        } catch (injectionError) {
          results.push({
            test: '强制注入后功能测试',
            status: 'error',
            message: '强制注入后仍然失败',
            details: injectionError
          });
        }
      }

      // Test 4: Check global polyfill status
      try {
        const globalObj = (function() {
          if (typeof globalThis !== 'undefined') return globalThis;
          if (typeof window !== 'undefined') return window;
          if (typeof global !== 'undefined') return global;
          if (typeof self !== 'undefined') return self;
          return null;
        })();

        if (globalObj) {
          const polyfillApplied = (globalObj as any).__PROMISE_WITH_RESOLVERS_POLYFILL_APPLIED__;
          const polyfillFailed = (globalObj as any).__PROMISE_WITH_RESOLVERS_POLYFILL_FAILED__;
          
          results.push({
            test: '全局 Polyfill 状态检查',
            status: polyfillApplied ? 'success' : polyfillFailed ? 'error' : 'warning',
            message: polyfillApplied 
              ? 'HTML 内联 polyfill 已成功应用' 
              : polyfillFailed 
                ? 'HTML 内联 polyfill 应用失败'
                : '未检测到 HTML 内联 polyfill 状态标记',
            details: { polyfillApplied, polyfillFailed }
          });
        }
      } catch (error) {
        results.push({
          test: '全局 Polyfill 状态检查',
          status: 'error',
          message: '状态检查失败',
          details: error
        });
      }

      // Test 5: Browser environment info
      try {
        const userAgent = navigator.userAgent;
        const isOldBrowser = /MSIE|Trident|Edge\/12|Edge\/13|Edge\/14|Edge\/15|Edge\/16|Edge\/17|Chrome\/[1-5][0-9]|Firefox\/[1-5][0-9]|Safari\/[1-9]/.test(userAgent);
        
        results.push({
          test: '浏览器环境信息',
          status: 'success',
          message: `浏览器: ${userAgent.split(' ').slice(-2).join(' ')}`,
          details: { 
            userAgent, 
            isOldBrowser,
            globalThis: typeof globalThis !== 'undefined',
            window: typeof window !== 'undefined',
            Promise: typeof Promise !== 'undefined',
            PromiseWithResolvers: typeof Promise !== 'undefined' && typeof Promise.withResolvers === 'function'
          }
        });
      } catch (error) {
        results.push({
          test: '浏览器环境信息',
          status: 'error',
          message: '环境信息获取失败',
          details: error
        });
      }

      setTestResults(results);
      setIsLoading(false);
    };

    runTests();
  }, []);

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Promise.withResolvers Polyfill 测试页面
          </h1>
          
          <div className="mb-6">
            <p className="text-gray-600">
              此页面用于测试 Promise.withResolvers polyfill 在当前浏览器环境下的工作状态。
              如果您在老版本浏览器中看到错误，这些测试结果将帮助诊断问题。
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">正在运行测试...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {testResults.map((result, index) => (
                <div key={index} className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}>
                  <div className="flex items-start space-x-3">
                    <span className="text-xl">{getStatusIcon(result.status)}</span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{result.test}</h3>
                      <p className="mt-1">{result.message}</p>
                      {result.details && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm font-medium">查看详细信息</summary>
                          <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                            {JSON.stringify(result.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">使用说明</h3>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• 绿色 ✅ 表示测试通过，功能正常</li>
              <li>• 黄色 ⚠️ 表示警告，可能需要注意</li>
              <li>• 红色 ❌ 表示测试失败，存在问题</li>
              <li>• 点击"查看详细信息"可以看到更多技术细节</li>
            </ul>
          </div>

          <div className="mt-6 text-center">
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              重新运行测试
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolyfillTestPage;